import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
} from 'antd';
import {PlusOutlined, InfoCircleOutlined, EditTwoTone} from '@ant-design/icons';
import {ColumnsType, TablePaginationConfig} from 'antd/lib/table';
import {useEffect, useState} from 'react';
import {useAppDispatch, useAppSelector} from '../../../common/hooks';
import {debounce} from 'lodash';
import {
  addVehicle,
  pageNumberChange,
  pageSizeChange,
  searchBy,
  sortBy,
  updateVehicle,
  vehiclesList,
  vehiclesStatusFilter,
} from './vehicleSlice';
import {
  batteryCategoryList,
  clearBatteryCategoryFilters,
} from '../battery/batteryCategory/batteryCategorySlice';
import {
  debounceDelay,
  sortbyFilter,
  statusFilter,
  statusFilterOptions,
} from '../../../common/constants';
import {ADD_VEHICLE, UPDATE_VEHICLE, VEHICLE_DATA} from './vehicle.type';
import checkFormUpdate from '../../../utils/checkFormUpdate';

const Vehicle = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((state) => state.vehicles);
  const userPermission = useAppSelector((state) => state.config.config.permissions);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [modalData, setModalData] = useState<VEHICLE_DATA>({} as VEHICLE_DATA);
  const batteryCategoriesOptions = useAppSelector((state) => state.batteryCategory);
  const [vehicleForm] = Form.useForm();

  const handleupdateVehicle = (values: UPDATE_VEHICLE) => {
    const payload = checkFormUpdate(values, modalData);

    if (Object.keys(payload).length > 0) {
      dispatch(updateVehicle({payload: payload, id: modalData.id}))
        .unwrap()
        .then(() => {
          handleVehicleModalClose();
          dispatch(
            vehiclesList({
              page_size: state.vehicles.page_size,
              page_number: state.vehicles.page_number,
              sort_by: state.vehicles.sort_by,
              ascending: state.vehicles.ascending,
              search: state.vehicles.search,
            }),
          );
        });
    } else {
      message.warning('No field Changed');
    }
  };

  const handleaddVehicle = (values: ADD_VEHICLE) => {
    dispatch(addVehicle(values))
      .unwrap()
      .then(() => {
        handleVehicleModalClose();
        dispatch(
          vehiclesList({
            page_size: state.vehicles.page_size,
            page_number: state.vehicles.page_number,
            sort_by: state.vehicles.sort_by,
            ascending: state.vehicles.ascending,
            search: state.vehicles.search,
          }),
        );
      });
  };

  const handleVehicleModalClose = () => {
    setIsVehicleModalOpen(false);
    vehicleForm.resetFields();
    dispatch(clearBatteryCategoryFilters());
  };

  const handleSearch = (e: string) => {
    if (e.length > 3) {
      dispatch(searchBy(e));
    } else if (e.length === 0) {
      dispatch(searchBy(e));
    }
  };

  const handleBatteryCategoryBouncing = debounce((e) => {
    dispatch(batteryCategoryList({search: e, is_active: true}));
  }, debounceDelay);

  useEffect(() => {
    //
    dispatch(
      vehiclesList({
        page_size: state.vehicles.page_size,
        page_number: state.vehicles.page_number,
        sort_by: state.vehicles.sort_by,
        ascending: state.vehicles.ascending,
        search: state.vehicles.search,
        is_active: state.vehicles.is_active,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.vehicles.page_number,
    state.vehicles.page_size,
    state.vehicles.search,
    state.vehicles.is_active,
    state.vehicles.sort_by,
    state.vehicles.ascending,
  ]);

  const columns: ColumnsType<VEHICLE_DATA> = [
    {
      title: 'S. No.',
      width: 70,
      render: (test, record, index) => {
        return <>{(state.vehicles.page_number - 1) * state.vehicles.page_size + index + 1}</>;
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: true,
    },
    {
      title: 'Voltage(V)',
      dataIndex: 'voltage',
      sorter: true,
    },
    {
      title: 'Current(A)',
      dataIndex: 'current',
      sorter: true,
    },
    {
      title: 'Power(W)',
      dataIndex: 'power',
      sorter: true,
    },
    {
      title: 'Battries No.',
      dataIndex: 'number_batteries',
    },

    {
      title: ' Status',
      dataIndex: 'is_active',
      render: (_, record) => {
        return record.is_active ? (
          <Tag color='success'>Active</Tag>
        ) : (
          <Tag color='error'>Inactive</Tag>
        );
      },
    },

    {
      title: 'Actions',
      key: 'operation',
      render: (_, record) => {
        return (
          <>
            <InfoCircleOutlined
              style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
              onClick={() => {
                setModalData(record);
                setIsInfoModalOpen(true);
              }}
            />
            {userPermission.update_vehicle && (
              <EditTwoTone
                style={{cursor: 'pointer', fontSize: '16px', color: 'green', marginInline: '10px'}}
                onClick={() => {
                  setModalData(record);
                  setIsUpdateModal(true);
                  const ids = record.supported_battery_categories.map((x) => x.id);

                  vehicleForm.setFieldsValue({
                    name: record.name,
                    power: record.power,
                    voltage: record.voltage,
                    current: record.current,
                    number_batteries: record.number_batteries,
                    supported_battery_categories: ids,
                    is_active: record.is_active,
                  });
                  setIsVehicleModalOpen(true);
                }}
              />
            )}
          </>
        );
      },
    },
  ];

  return (
    <div>
      <div
        style={{
          margin: '10px 0px 10px 0px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '350px  1fr   1fr',
        }}
      >
        <Input.Search
          placeholder='Search'
          defaultValue={state.vehicles.search}
          style={{borderRadius: '10px'}}
          allowClear
          onSearch={(e) => handleSearch(e)}
        />
        <Select
          style={{width: '100px', marginLeft: '5px'}}
          value={
            state.vehicles.is_active === undefined
              ? statusFilter.ALL
              : state.vehicles.is_active === true
              ? statusFilter.ACTIVE
              : statusFilter.INACTIVE
          }
          options={statusFilterOptions}
          onChange={(value) => {
            if (value === statusFilter.ACTIVE) {
              dispatch(vehiclesStatusFilter(true));
            } else if (value === statusFilter.ALL) {
              dispatch(vehiclesStatusFilter(undefined));
            } else {
              dispatch(vehiclesStatusFilter(false));
            }
          }}
          showArrow
        />
        <div style={{width: '100%', textAlign: 'end'}}>
          {userPermission.add_vehicle && (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              style={{borderRadius: '5px', marginInline: '5px'}}
              onClick={() => {
                setIsUpdateModal(false);
                dispatch(batteryCategoryList({is_active: true}));
                setIsVehicleModalOpen(true);
              }}
            >
              Add Vehicle
            </Button>
          )}
        </div>
      </div>
      <Table
        rowKey='id'
        columns={columns}
        pagination={
          {
            total: state.vehicles.totalDataCount,
            current: state.vehicles.page_number,
            pageSize: state.vehicles.page_size,
            showSizeChanger: true,
            // onChange: (pageNumber, pageSize) => {
            //   if (pageSize !== state.vehicles.page_size) {
            //     dispatch(pageSizeChange({pageNumber, pageSize}));
            //   } else {
            //     dispatch(pageNumberChange(pageNumber));
            //   }
            // },
          } as TablePaginationConfig
        }
        loading={state.isLoading}
        size='middle'
        dataSource={state.vehicles.pageData}
        scroll={{x: undefined, y: '69vh'}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange={(pagination, _, {order, field}: any) => {
          if (pagination.pageSize && pagination.pageSize !== state.vehicles.page_size) {
            dispatch(pageSizeChange(pagination.pageSize));
          }
          if (pagination.current && pagination.current !== state.vehicles.page_number) {
            dispatch(pageNumberChange(pagination.current));
          }
          if (field) {
            if (order === sortbyFilter.DESCEND) {
              dispatch(sortBy({ascending: false, sortBy: field}));
            } else {
              dispatch(sortBy({ascending: true, sortBy: field}));
            }
          }
        }}
      />
      {isInfoModalOpen && (
        <Modal
          title={`${modalData?.name}`}
          open={isInfoModalOpen}
          onCancel={() => {
            setIsInfoModalOpen(false);
          }}
          footer={null}
          width={800}
        >
          <Descriptions bordered column={2} labelStyle={{fontWeight: 'bolder'}}>
            <Descriptions.Item label='Vehicle Name'>{modalData?.name}</Descriptions.Item>
            <Descriptions.Item label='Vehicle Id'>{modalData?.id}</Descriptions.Item>

            <Descriptions.Item label='Is Active' span={2}>
              {modalData?.is_active ? (
                <Tag color='success'>Active</Tag>
              ) : (
                <Tag color='error'> Inactive</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label='Created By'>{modalData?.created_by}</Descriptions.Item>
            <Descriptions.Item label='Created At'>{modalData?.created_at}</Descriptions.Item>
            <Descriptions.Item label='Updated By'>{modalData?.updated_by}</Descriptions.Item>
            <Descriptions.Item label='Updated At'>{modalData?.updated_at}</Descriptions.Item>
            <Descriptions.Item label=' Power '>{modalData?.power}</Descriptions.Item>
            <Descriptions.Item label=' Voltage '>{modalData?.voltage}</Descriptions.Item>
            <Descriptions.Item label=' Current '>{modalData?.voltage}</Descriptions.Item>
            <Descriptions.Item label=' No. o Batteries '>
              {modalData?.number_batteries}
            </Descriptions.Item>
            <Descriptions.Item label=' Supported Battery Categories '>
              {modalData?.supported_battery_categories?.map((x) => {
                return <>{x.name}, </>;
              })}
            </Descriptions.Item>
          </Descriptions>
        </Modal>
      )}
      <Modal
        title={isUpdateModal ? 'Update Vehicle' : 'Add Vehicle'}
        open={isVehicleModalOpen}
        maskClosable={false}
        onCancel={() => {
          handleVehicleModalClose();
        }}
        okText={isUpdateModal ? 'Update Vehicle' : 'Add Vehicle'}
        onOk={() => {
          vehicleForm
            .validateFields()
            .then((values) => {
              isUpdateModal ? handleupdateVehicle(values) : handleaddVehicle(values);
            })
            .catch((info) => {
              console.error('Validate Failed:', info);
            });
        }}
      >
        <Row>
          <Col span={24}>
            <Form name='updateVehicle' form={vehicleForm} layout='vertical'>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='name'
                    label=' Name'
                    rules={[
                      {required: true, message: 'Please fill Vehicle Name '},
                      {min: 4, message: 'Minimum length is 4 characters'},
                    ]}
                  >
                    <Input autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='number_batteries'
                    label='No. of  Battery '
                    rules={[{required: true, message: ' Fill No. of batteries'}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Form.Item
                    name='supported_battery_categories'
                    label='Battery Categories '
                    rules={[{required: true, message: 'Select atleast 1 option'}]}
                  >
                    <Select
                      mode='multiple'
                      allowClear
                      placeholder='Please select'
                      listHeight={200}
                      showSearch
                      defaultActiveFirstOption={false}
                      showArrow={false}
                      filterOption={false}
                      onSearch={(e) => {
                        if (e.length > 3) handleBatteryCategoryBouncing(e);
                      }}
                      // onChange={handleChange}
                      notFoundContent={null}
                      loading={batteryCategoriesOptions.isLoading}
                    >
                      {batteryCategoriesOptions.batteryCategories.pageData.map((x) => (
                        <Select.Option key={x.id} value={x.id}>
                          {x.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='current'
                    label={
                      <>
                        Current <span style={{color: '#1890FF'}}>(A)</span>
                      </>
                    }
                    rules={[{required: true, message: ' Current '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
                <Col span={11} offset={2}>
                  <Form.Item
                    name='voltage'
                    label={
                      <>
                        Voltage <span style={{color: '#1890FF'}}>(V)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Voltage '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={11}>
                  <Form.Item
                    name='power'
                    label={
                      <>
                        Power <span style={{color: '#1890FF'}}>(W)</span>
                      </>
                    }
                    rules={[{required: true, message: 'Please fill Power '}]}
                  >
                    <InputNumber style={{width: '100%'}} autoComplete='off' />
                  </Form.Item>
                </Col>

                {isUpdateModal && (
                  <Col span={11} offset={2}>
                    <Form.Item name='is_active' label='Status' valuePropName='checked'>
                      <Switch checkedChildren='Active' unCheckedChildren='Inactive' />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            </Form>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default Vehicle;
